import { ArrowDownRightIcon, ArrowUpRightIcon, MinusIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { fmtNumber, fmtPercent } from '@/lib/format';
import type { OverviewKpis, OverviewPoint } from '@/lib/reports';
import { cn } from '@/lib/utils';
import { Sparkline } from './charts';

/**
 * The six numbers a Super User reads first, each against the equally long period before: texts sent, people
 * reached, delivery rate, failed texts, contacts added, opt-outs. Direction colour respects polarity: fewer
 * failures and fewer opt-outs are good.
 */

export interface KpiDef {
  key: string;
  label: string;
  value: (k: OverviewKpis) => number;
  format?: 'number' | 'percent';
  /** When up is bad (failures, opt-outs). */
  invert?: boolean;
  spark: (p: OverviewPoint) => number;
  hint?: (k: OverviewKpis) => string | undefined;
}

export const KPIS: KpiDef[] = [
  { key: 'messages', label: 'Texts sent', value: (k) => k.messages, spark: (p) => p.messages, hint: (k) => `${fmtNumber(k.attempted)} ${k.attempted === 1 ? 'text' : 'texts'} attempted` },
  { key: 'recipients', label: 'People reached', value: (k) => k.recipients, spark: (p) => p.recipients, hint: (k) => (k.messages ? `${fmtNumber(Math.round(k.recipients / k.messages))} per message` : undefined) },
  { key: 'rate', label: 'Delivery rate', value: (k) => (k.attempted ? (k.delivered / k.attempted) * 100 : 0), format: 'percent', spark: (p) => (p.delivered + p.failed ? (p.delivered / (p.delivered + p.failed)) * 100 : 0), hint: (k) => (k.attempted ? `${fmtNumber(k.delivered)} of ${fmtNumber(k.attempted)} delivered` : 'no texts attempted') },
  { key: 'failed', label: 'Failed texts', value: (k) => k.failed, invert: true, spark: (p) => p.failed, hint: (k) => (k.attempted ? `${fmtPercent((k.failed / k.attempted) * 100)} of attempted` : undefined) },
  { key: 'added', label: 'Contacts added', value: (k) => k.contactsAdded, spark: (p) => p.contactsAdded, hint: (k) => (k.joinsCompleted ? `${fmtNumber(k.joinsCompleted)} joined by text` : undefined) },
  { key: 'optouts', label: 'Opt-outs', value: (k) => k.optOuts, invert: true, spark: (p) => p.optOuts, hint: (k) => (k.optIns ? `${fmtNumber(k.optIns)} opted back in` : undefined) },
];

/**
 * Change against the previous period. Percent when the baseline is meaningful (≥ 10), the signed absolute
 * difference when it is small (a jump from 2 to 14 reads as "+12", not "+600%"); percentage points for rates.
 */
export function Delta({ current, previous, invert, percentPoints, previousLabel }: { current: number; previous: number; invert?: boolean; percentPoints?: boolean; previousLabel: string }) {
  let text: string;
  let dir: 'up' | 'down' | 'flat';
  if (percentPoints) {
    const d = current - previous;
    dir = Math.abs(d) < 0.05 ? 'flat' : d > 0 ? 'up' : 'down';
    text = dir === 'flat' ? 'no change' : `${Math.abs(d).toFixed(1)} pts`;
  } else if (previous < 10) {
    const d = current - previous;
    dir = d === 0 ? 'flat' : d > 0 ? 'up' : 'down';
    text = dir === 'flat' ? 'no change' : `${d > 0 ? '+' : '−'}${fmtNumber(Math.abs(d))}`;
  } else {
    const pct = ((current - previous) / previous) * 100;
    dir = Math.abs(pct) < 0.5 ? 'flat' : pct > 0 ? 'up' : 'down';
    text = dir === 'flat' ? 'no change' : `${Math.abs(pct) >= 1000 ? '>999' : Math.abs(pct).toFixed(Math.abs(pct) >= 10 ? 0 : 1)}%`;
  }
  const good = dir === 'flat' ? null : (dir === 'up') !== !!invert;
  const Icon = dir === 'up' ? ArrowUpRightIcon : dir === 'down' ? ArrowDownRightIcon : MinusIcon;
  return (
    <span className="flex flex-col items-start gap-1 text-xs" title={`Compared with ${previousLabel}`}>
      <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 font-medium whitespace-nowrap tabular-nums', good === null ? 'bg-muted text-muted-foreground' : good ? 'bg-success/15 text-success' : 'bg-destructive/10 text-destructive')}>
        <Icon className="size-3" aria-hidden />
        {text}
      </span>
      <span className="text-muted-foreground truncate">vs {previousLabel}</span>
    </span>
  );
}

export function KpiBand({ kpis, series, previousLabel, loading }: { kpis?: { current: OverviewKpis; previous: OverviewKpis }; series?: OverviewPoint[]; previousLabel: string; loading?: boolean }) {
  return (
    <div className="bg-border grid grid-cols-2 gap-px border md:grid-cols-3 xl:grid-cols-6" role="list" aria-label="Headline numbers">
      {KPIS.map((d) => {
        const cur = kpis ? d.value(kpis.current) : null;
        const prev = kpis ? d.value(kpis.previous) : null;
        const points = series ? series.map(d.spark) : [];
        return (
          <div key={d.key} role="listitem" className="bg-card flex min-w-0 flex-col gap-2 p-4 lg:p-5">
            <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{d.label}</span>
            {loading || cur === null || prev === null || !kpis ? (
              <>
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-4 w-28" />
              </>
            ) : (
              <>
                <span className="font-display text-4xl leading-none tracking-tight">{d.format === 'percent' ? (kpis.current.attempted ? fmtPercent(cur) : '—') : fmtNumber(cur)}</span>
                <Delta current={cur} previous={prev} invert={d.invert} percentPoints={d.format === 'percent'} previousLabel={previousLabel} />
              </>
            )}
            <Sparkline points={points} bad={d.invert} className="mt-1" />
            <span className="text-muted-foreground min-h-4 text-xs">{kpis ? d.hint?.(kpis.current) ?? '' : ''}</span>
          </div>
        );
      })}
    </div>
  );
}
